import EventCardsTypes from 'modules/event-cards/types.js';

const { DARK } = EventCardsTypes.COLOR_MODES;

/**
 * Utility functions shared by the prebuilt card views.
 *
 * @class PrebuiltBetUtils
 * @name utils
 * @memberof Mojito.Modules.PrebuiltBets
 */
export default class PrebuiltBetUtils {
    /**
     * Builds the card details for every colour mode. Intended to be called from a classic prebuilt card's `getStyle`.
     * The dark details are a partial override merged on top of the regular details.
     *
     * @param {object} config - Card config.
     * @param {string} darkDetailsKey - Name of the config node that holds the dark colour mode overrides.
     * @param {Function} merge - Merge function provided to `getStyle`.
     *
     * @returns {object} Card details keyed by colour mode.
     * @function Mojito.Modules.PrebuiltBets.utils.buildCardDetails
     */
    static buildCardDetails(config, darkDetailsKey, merge) {
        const { [darkDetailsKey]: darkOverrides, ...details } = config;

        return {
            light: details,
            dark: merge(details, darkOverrides || {}),
        };
    }

    /**
     * Picks the card details built by {@link Mojito.Modules.PrebuiltBets.utils.buildCardDetails|buildCardDetails} for the given colour mode.
     *
     * @param {object} cardDetails - Card details built by `buildCardDetails`.
     * @param {Mojito.Modules.EventCards.types.COLOR_MODES} colorMode - Colour mode.
     *
     * @returns {object} Card details.
     * @function Mojito.Modules.PrebuiltBets.utils.resolveCardDetails
     */
    static resolveCardDetails(cardDetails, colorMode) {
        return colorMode === DARK ? cardDetails.dark : cardDetails.light;
    }

    /**
     * Returns the early payout of a market, or undefined when it has no early payout rules.
     *
     * @param {object} [market] - Market.
     *
     * @returns {object|undefined} Early payout.
     * @function Mojito.Modules.PrebuiltBets.utils.getEarlyPayout
     */
    static getEarlyPayout(market) {
        return market?.earlyPayout?.rules?.length ? market.earlyPayout : undefined;
    }
}
